/**
 * Enrollment Status Utilities
 *
 * Status badge mappings and helper functions for payer enrollments.
 * Based on the backend integration guide status definitions.
 */

import type { PayerResponse } from '@store/generated/payers';

// MUI Chip color types
type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

/**
 * Enrollment status configuration
 * Maps backend status values to UI display properties
 */
export const ENROLLMENT_STATUS_CONFIG: Record<
  string,
  { color: ChipColor; label: string; description: string }
> = {
  DRAFT: {
    color: 'default',
    label: 'Draft',
    description: 'Enrollment not yet submitted',
  },
  STEDI_ACTION_REQUIRED: {
    color: 'warning',
    label: 'Submitted',
    description: 'Waiting for clearinghouse processing',
  },
  PROVISIONING: {
    color: 'warning',
    label: 'Pending',
    description: 'Enrollment is being processed by the payer',
  },
  PROVIDER_ACTION_REQUIRED: {
    color: 'warning',
    label: 'Action Required',
    description: 'You need to complete additional steps',
  },
  LIVE: {
    color: 'success',
    label: 'Active',
    description: 'Enrollment is active - claims can be submitted',
  },
  REJECTED: {
    color: 'error',
    label: 'Rejected',
    description: 'Enrollment was rejected by the payer',
  },
  CANCELED: {
    color: 'default',
    label: 'Canceled',
    description: 'Enrollment was canceled',
  },
};

/**
 * Transaction support level configuration
 * Maps backend support levels to UI display properties
 */
export const SUPPORT_LEVEL_CONFIG: Record<
  string,
  { color: ChipColor; label: string; description: string }
> = {
  SUPPORTED: {
    color: 'success',
    label: 'Ready',
    description: 'No enrollment required - claims can be submitted immediately',
  },
  ENROLLMENT_REQUIRED: {
    color: 'warning',
    label: 'Enrollment Required',
    description: 'Must enroll before claims can be submitted',
  },
  NOT_SUPPORTED: {
    color: 'error',
    label: 'Not Available',
    description: "This payer doesn't accept electronic claims",
  },
};

/**
 * Get the status configuration for an enrollment status
 */
export function getEnrollmentStatusConfig(status: string | null | undefined) {
  if (!status) {
    return ENROLLMENT_STATUS_CONFIG.DRAFT;
  }
  return ENROLLMENT_STATUS_CONFIG[status] || ENROLLMENT_STATUS_CONFIG.DRAFT;
}

/**
 * Get the support level configuration for a transaction support level
 */
export function getSupportLevelConfig(supportLevel: string | null | undefined) {
  if (!supportLevel) {
    return SUPPORT_LEVEL_CONFIG.NOT_SUPPORTED;
  }
  return SUPPORT_LEVEL_CONFIG[supportLevel] || SUPPORT_LEVEL_CONFIG.NOT_SUPPORTED;
}

/**
 * Get the display label for a transaction type
 */
export function getTransactionTypeLabel(transactionType: string): string {
  switch (transactionType) {
    case '837P':
      return 'Claims';
    case '835':
      return 'ERA';
    default:
      return transactionType;
  }
}

/**
 * Get enrollment timeframe display string from payer enrollment info
 */
export function getEnrollmentTimeframe(
  payer: PayerResponse,
  transactionType: string = 'professionalClaimSubmission',
): string {
  const info = payer.enrollment_info?.[transactionType];

  if (!info) {
    return 'Timeline varies';
  }

  const enrollmentType = info.type as string | undefined;
  const timeframe = info.timeframe as string | undefined;

  if (enrollmentType === 'ONE_CLICK') {
    return 'One-click enrollment • Usually 1-2 days';
  }

  switch (timeframe) {
    case 'DAYS':
      return 'Usually 3-5 business days';
    case 'WEEKS':
      return 'Usually 1-2 weeks';
    case 'MONTHS':
      return 'May take up to 30 days';
    default:
      return 'Timeline varies';
  }
}

/**
 * Get the professional claim submission support level for a payer
 */
export function getClaimSubmissionSupport(payer: PayerResponse): string {
  return payer.transaction_support?.professionalClaimSubmission || 'NOT_SUPPORTED';
}

/**
 * Check if an enrollment status is in a pending/processing state
 */
export function isPendingStatus(status: string): boolean {
  return ['STEDI_ACTION_REQUIRED', 'PROVISIONING', 'PROVIDER_ACTION_REQUIRED', 'DRAFT'].includes(
    status,
  );
}

/**
 * Check if an enrollment status is terminal (no further changes expected)
 */
export function isTerminalStatus(status: string): boolean {
  return ['LIVE', 'REJECTED', 'CANCELED'].includes(status);
}

/**
 * Check if a payer requires enrollment for claim submission
 */
export function requiresEnrollment(payer: PayerResponse): boolean {
  const support = getClaimSubmissionSupport(payer);
  return support === 'ENROLLMENT_REQUIRED';
}

/**
 * Check if claims can be submitted to a payer without enrollment
 */
export function isReadyForClaims(payer: PayerResponse): boolean {
  const support = getClaimSubmissionSupport(payer);
  return support === 'SUPPORTED';
}
